import { SEOHead } from "components/SEO"
import { generateProductPageGraphSchema } from "utils/structuredData"
import ArchitectureSection from "components/pages/preset-managed-private-cloud/ArchitectureSection"
import DataAndCloudSecuritySection from "components/pages/preset-managed-private-cloud/DataAndCloudSecuritySection"
import HeroSection from "components/pages/preset-managed-private-cloud/HeroSection"
import OurServiceSection from "components/pages/preset-managed-private-cloud/OurServiceSection"
import PresetForPrivateCloudSection from "components/pages/preset-managed-private-cloud/PresetForPrivateCloudSection"
import FAQSection from "components/pages/preset-managed-private-cloud/FAQSection"
import SingleCTASection from "components/pages/preset-managed-private-cloud/SingleCTASection"
import Layout from "components/ui/Layout"

// FAQs for managed private cloud (used for schema markup)
const privateCloudFaqs = [
  {
    question: "What is Preset Managed Private Cloud?",
    answer:
      "Preset Managed Private Cloud deploys the Preset platform in your own cloud environment while Preset manages the infrastructure, updates, and maintenance.",
  },
  {
    question: "How is Managed Private Cloud different from Preset Cloud?",
    answer:
      "Preset Cloud runs on Preset's multi-tenant infrastructure, while Managed Private Cloud runs in your own cloud account for greater data residency and environment control.",
  },
  {
    question: "What cloud providers does Preset Managed Private Cloud support?",
    answer:
      "Preset Managed Private Cloud supports AWS, Google Cloud Platform, and Microsoft Azure.",
  },
  {
    question: "Is Managed Private Cloud HIPAA compliant?",
    answer:
      "Preset Managed Private Cloud supports HIPAA-eligible deployments for organizations with strict regulatory requirements.",
  },
  {
    question: "Who manages the infrastructure in Managed Private Cloud?",
    answer:
      "Preset manages provisioning, updates, scaling, monitoring, and maintenance, while the customer provides the cloud account and network access.",
  },
  {
    question: "Can I use private networking with Managed Private Cloud?",
    answer:
      "Yes. Managed Private Cloud runs inside your own AWS, GCP, or Azure account, keeping traffic within your virtual network, with private networking options such as VPC peering and AWS PrivateLink.",
  },
  {
    question: "What security certifications does Preset have?",
    answer:
      "Preset is SOC 2 Type II certified, and Managed Private Cloud supports customer-managed encryption keys, SSO or SAML authentication, audit logging, and HIPAA-eligible configurations.",
  },
  {
    question: "How does billing work for Managed Private Cloud?",
    answer:
      "Managed Private Cloud is available on Enterprise plans with custom pricing, with Preset billing for the managed service and the customer paying their cloud provider for infrastructure.",
  },
]

function PresetManagedPrivateCloud({ location: { pathname } }) {
  return (
    <Layout
      contentSpaced
      trackingName="Preset Managed Private Cloud"
      pageName="preset-managed-private-cloud"
      pagePath={pathname}
    >
      <HeroSection />
      <PresetForPrivateCloudSection />
      <OurServiceSection />
      <ArchitectureSection />
      <DataAndCloudSecuritySection />
      <FAQSection />
      <SingleCTASection />
    </Layout>
  )
}

// Export a Head component for Gatsby Head API
export function Head() {
  const graphSchema = generateProductPageGraphSchema({
    pageUrl: "https://preset.io/preset-managed-private-cloud/",
    pageTitle: "Managed Private Cloud Solution | Preset",
    productName: "Preset Managed Private Cloud",
    description:
      "Preset Managed Private Cloud deploys the Preset analytics platform in your own cloud environment, giving teams managed Apache Superset with stronger data residency, private networking, and enterprise-grade security controls.",
    featureList: [
      "Managed Apache Superset in your cloud",
      "Deployment in AWS, Google Cloud, or Azure",
      "Data stays inside your VPC",
      "Private networking options",
      "Role-based access control",
      "Row-level security",
      "Access and audit logs",
      "Encryption in transit and at rest",
      "Multi-factor authentication",
      "HIPAA-eligible deployment options",
      "SOC 2 Type II certified provider support",
      "Managed updates, scaling, monitoring, and maintenance",
    ],
    faqs: privateCloudFaqs,
    offers: {
      "@type": "Offer",
      url: "https://preset.io/preset-managed-private-cloud/",
      priceSpecification: {
        "@type": "PriceSpecification",
        priceCurrency: "USD",
      },
      availability: "https://schema.org/InStock",
      category: "Enterprise SaaS",
      description:
        "Managed Private Cloud is available on Enterprise plans with custom pricing.",
    },
    breadcrumbs: [
      { name: "Home", url: "https://preset.io/" },
      {
        name: "Managed Private Cloud",
        url: "https://preset.io/preset-managed-private-cloud/",
      },
    ],
  })

  return (
    <SEOHead
      title="Managed Private Cloud BI & Analytics Platform"
      description="Preset's modern BI platform now available in your managed private cloud. Build and share dashboards securely. We take care of the rest."
      structuredData={[graphSchema]}
    />
  )
}

export default PresetManagedPrivateCloud
